import { useEffect, useRef } from "react";

import { useLazyGetBookmarksQuery } from "apis/dataV2/dataV2Api";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useSelectBookmark } from "features/bookmarks/useSelectBookmark";
import { useSceneId } from "hooks/useSceneId";
import { explorerActions, selectLocalBookmarkId, selectUrlBookmarkId } from "slices/explorer";
import { telemetry } from "telemetry";

import { selectAutofreezeEnabled } from "../renderSlice";

export function useHandleInitialBookmark() {
    const sceneId = useSceneId();
    const {
        state: { view },
    } = useExplorerGlobals();

    const urlBookmarkId = useAppSelector(selectUrlBookmarkId);
    const localBookmarkId = useAppSelector(selectLocalBookmarkId);
    const selectBookmark = useSelectBookmark();
    const dispatch = useAppDispatch();
    const [getBookmarks] = useLazyGetBookmarksQuery();
    const autofreezeEnabled = useAppSelector(selectAutofreezeEnabled);
    const autofreezeEnabledRef = useRef(autofreezeEnabled);
    autofreezeEnabledRef.current = autofreezeEnabled;

    useEffect(() => {
        handleUrlBookmark();

        async function handleUrlBookmark() {
            if (!view || !urlBookmarkId) {
                return;
            }

            dispatch(explorerActions.setUrlBookmarkId(undefined));

            try {
                const groupResult = await getBookmarks({ projectId: sceneId, group: urlBookmarkId }, true).unwrap();
                const shareLinkBookmark = groupResult.bookmarks.find((bm) => bm.id === urlBookmarkId);

                if (shareLinkBookmark) {
                    selectBookmark({
                        bookmark: shareLinkBookmark,
                        shareLinkIdMap: groupResult.resolvedPublicIds,
                        autofreezeEnabled: autofreezeEnabledRef.current,
                    });
                    return;
                }

                const publicResult = await getBookmarks({ projectId: sceneId }, true).unwrap();
                const savedPublicBookmark = publicResult.bookmarks.find((bm) => bm.id === urlBookmarkId);
                if (savedPublicBookmark) {
                    selectBookmark({
                        bookmark: savedPublicBookmark,
                        shareLinkIdMap: publicResult.resolvedPublicIds,
                        autofreezeEnabled: autofreezeEnabledRef.current,
                    });
                }
            } catch (e) {
                console.warn(e);
                telemetry.trackCaughtException({
                    source: "render.useHandleInitialBookmark.handleUrlBookmark",
                    category: "init",
                    error: e,
                });
            }
        }
    }, [view, sceneId, dispatch, selectBookmark, urlBookmarkId, getBookmarks]);

    useEffect(() => {
        handleLocalBookmark();

        function handleLocalBookmark() {
            if (!view || !localBookmarkId) {
                return;
            }

            dispatch(explorerActions.setLocalBookmarkId(undefined));

            try {
                const storedBm = sessionStorage.getItem(localBookmarkId);

                if (!storedBm) {
                    return;
                }

                sessionStorage.removeItem(localBookmarkId);
                const bookmark = JSON.parse(storedBm);

                if (!bookmark) {
                    return;
                }

                selectBookmark({ bookmark, autofreezeEnabled: autofreezeEnabledRef.current });
            } catch (e) {
                console.warn(e);
                telemetry.trackCaughtException({
                    source: "render.useHandleInitialBookmark.handleLocalBookmark",
                    category: "init",
                    error: e,
                });
            }
        }
    }, [view, sceneId, dispatch, selectBookmark, localBookmarkId]);
}
